import * as React from 'react';
import {
    AddedArticleIcon,
    addIcon,
    alertErrorIcon,
    discountIcon,
    FaOptionIcon,
    hiddenArticleIcon,
    NamedIconsType,
    openIcon,
} from '@europrocurement/l2d-icons';
import { Box, Card, Palette, PaletteColor, Typography } from '@mui/material';
import { SortableItem, SortableList } from '@europrocurement/l2d-modules/modules/Offres';
import {
    ArticleTarifDTOJsonldDonneesFacturationRead,
    DossierTarifDTOJsonldDonneesFacturationRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import {
    Accordion,
    FlexyButton,
    ListCardBone,
    SkeletonLoader,
    Switch,
    useModal,
} from '@europrocurement/flexy-components';
import { CollectionDeDossierJsonldCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DossiersArticlesReductions } from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierPrefacturation';
import { Link } from 'react-router-dom';
import {
    detailDossierPath,
    dossiersBasePath,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants';
import ArticlePrefacturationHeader from '../../../ArticlePrefacturationHeader/ArticlePrefacturationHeader';
import ArticlePrefacturation from '../../../ArticlePrefacturation/ArticlePrefacturation';
import NoResult from '../../../NoResult/NoResult';
import ArticleAdd from '../../../ArticleAdd/ArticleAdd';

type OrderDetailsSectionProps = {
    isTech: boolean;
    remisesInvisiblesAffichees: boolean;
    setRemisesInvisiblesAffichees: React.Dispatch<React.SetStateAction<boolean>>;
    collections: CollectionDeDossierJsonldCollectiondossierRead[];
    dataPrefacturation: DossiersArticlesReductions;
    dataPrefacturationDossiers: DossierTarifDTOJsonldDonneesFacturationRead[];
    palette: Palette;
    afterSubmit: () => void;
    sortableListOnChange: (
        items: (Omit<ArticleTarifDTOJsonldDonneesFacturationRead, 'id'> & {
            id: number;
        })[],
        dossier: DossierTarifDTOJsonldDonneesFacturationRead,
        dossierIdx: number,
    ) => void;
    onChangeDossiersSelected?: (id: number, checked: boolean) => void;
    dossiersSelected?: Array<number>;
};

const OrderDetailsSection: React.FunctionComponent<OrderDetailsSectionProps> = function ({
    isTech,
    remisesInvisiblesAffichees,
    setRemisesInvisiblesAffichees,
    collections,
    dataPrefacturation,
    dataPrefacturationDossiers,
    palette,
    afterSubmit,
    sortableListOnChange,
    onChangeDossiersSelected,
    dossiersSelected,
}: OrderDetailsSectionProps) {
    const { modalActions } = useModal();

    const getArticleIcon = (
        article: Omit<ArticleTarifDTOJsonldDonneesFacturationRead, 'id'> & {
            id: number;
        },
    ): NamedIconsType | undefined => {
        if (remisesInvisiblesAffichees && article.fAjouteCalcul) {
            return AddedArticleIcon;
        }
        if (!article.articleFacturable && !article.articleFacture) {
            return alertErrorIcon;
        }
        return undefined;
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: 'none',
                padding: 0,
            }}
            id="DetailsDeLaCommande"
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Détails de la commande"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        {isTech ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    variant="l2dh7"
                                    sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                                >
                                    Afficher articles cachés
                                </Typography>
                                <Switch
                                    value={remisesInvisiblesAffichees}
                                    checked={remisesInvisiblesAffichees}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Évite de déclencher l'ouverture ou la fermeture de l'accordéon
                                    }}
                                    onChange={(e, value) => {
                                        setRemisesInvisiblesAffichees(value);
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Box>
                }
            >
                <SkeletonLoader
                    isLoading={!(collections && dataPrefacturationDossiers)}
                    skeleton={<ListCardBone />}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            boxShadow: 'none',
                            padding: 0,
                        }}
                    >
                        {collections &&
                            dataPrefacturationDossiers.map((dossier, dossierIdx) => (
                                <Box>
                                    <Accordion
                                        sx={{
                                            boxShadow: 'none',
                                        }}
                                        title={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {dossier.prestations
                                                    ?.map((prestation) => prestation.libelle)
                                                    .join(' - ') || ''}
                                                {!dossier.dossierFacturable &&
                                                !dossier.dossierFacture ? (
                                                    <FaOptionIcon
                                                        {...alertErrorIcon.props}
                                                        color="text.gris35"
                                                        tooltip={dossier.motifNonFacturable}
                                                    />
                                                ) : null}

                                                <Link
                                                    to={`/${dossiersBasePath}/${detailDossierPath.replace(':idDossier', dossier.id as unknown as string)}`}
                                                    target="_blank"
                                                    style={{
                                                        textDecoration: 'none',
                                                        color: palette.grey[400],
                                                    }}
                                                >
                                                    <FaOptionIcon
                                                        {...openIcon.props}
                                                        size="sm"
                                                    />
                                                </Link>
                                            </Box>
                                        }
                                        titlesSx={{
                                            fontWeight: '600',
                                        }}
                                        subtitle={`Ref. Dossier #${dossier.numero}`}
                                        expanded
                                        expandColor={
                                            palette.text.primary as unknown as PaletteColor
                                        }
                                        expandLabel={
                                            <>
                                                <Typography
                                                    variant="l2dlabel"
                                                    sx={{ color: 'text.gris70' }}
                                                >
                                                    Total TTC :
                                                </Typography>
                                                <Typography
                                                    variant="l2dh7"
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontWeight: '600',
                                                    }}
                                                >{` ${dossier.ttc} €`}</Typography>
                                                {dossier.resteAFacturer !== undefined &&
                                                Number(dossier.resteAFacturer) > 0 ? (
                                                    <>
                                                        <Typography
                                                            variant="l2dlabel"
                                                            sx={{ color: 'text.gris70' }}
                                                        >
                                                            Reste à facturer :
                                                        </Typography>
                                                        <Typography
                                                            variant="l2dh7"
                                                            sx={{
                                                                color: 'text.primary',
                                                                fontWeight: '600',
                                                            }}
                                                        >{` ${dossier.resteAFacturer} €`}</Typography>
                                                    </>
                                                ) : null}
                                            </>
                                        }
                                        footerActions={
                                            dossier.prestations && [
                                                <FlexyButton
                                                    variant="outlined"
                                                    color={palette.primary.dark}
                                                    onClick={() => {
                                                        if (!dossier.prestations) {
                                                            throw new Error(
                                                                'dossier.prestations is undefined',
                                                            );
                                                        }
                                                        if (!dataPrefacturation.tauxTvas) {
                                                            throw new Error(
                                                                'dataPrefacturation.tauxTvas is undefined',
                                                            );
                                                        }

                                                        modalActions.call(
                                                            <ArticleAdd
                                                                tauxTva={
                                                                    dataPrefacturation.tauxTvas
                                                                }
                                                                prestations={dossier.prestations}
                                                                afterSubmit={afterSubmit}
                                                            />,
                                                        );
                                                    }}
                                                    icon={addIcon}
                                                    label="Ajouter un article"
                                                    disabled={
                                                        dossier.articles?.some(
                                                            (article) =>
                                                                article.articleFacture &&
                                                                !article.articleFactureValidee,
                                                        ) ?? false
                                                    }
                                                />,
                                            ]
                                        }
                                        checkBox={(dataPrefacturation.dossiers?.length ?? 0) > 1}
                                        checkBoxDisabled={!dossier.dossierFacturable}
                                        checked={
                                            dossier.id !== undefined &&
                                            dossiersSelected &&
                                            dossiersSelected.includes(dossier.id)
                                        }
                                        onChange={(checked) => {
                                            console.log(checked);
                                            console.log(dossier.id);
                                            if (
                                                dossier.id !== undefined &&
                                                onChangeDossiersSelected
                                            ) {
                                                console.log('onChangeDossiersSelected');
                                                onChangeDossiersSelected(dossier.id, checked);
                                            }
                                            console.log('apres onChangeDossiersSelected');
                                        }}
                                    >
                                        <ArticlePrefacturationHeader />

                                        {dossier.articles ? (
                                            <SortableList
                                                style={{ margin: '0px' }}
                                                items={
                                                    dossier.articles.filter(
                                                        (art) =>
                                                            art.id !== undefined &&
                                                            !art.fMasqueCalcul,
                                                    ) as Array<
                                                        Omit<
                                                            ArticleTarifDTOJsonldDonneesFacturationRead,
                                                            'id'
                                                        > & {
                                                            id: number;
                                                        }
                                                    >
                                                }
                                                onChange={(items) => {
                                                    sortableListOnChange(
                                                        items,
                                                        dossier,
                                                        dossierIdx,
                                                    );
                                                }}
                                                renderItem={(article) => {
                                                    const key = `facturation_collection_article_${article.id}`;
                                                    const articleIcon = getArticleIcon(article);
                                                    return (
                                                        <SortableItem
                                                            id={article.id}
                                                            key={key}
                                                        >
                                                            <ArticlePrefacturation
                                                                article={article}
                                                                afterSubmit={afterSubmit}
                                                                sortabled
                                                                icon={articleIcon}
                                                                iconTooltip={
                                                                    article.motifNonFacturable
                                                                }
                                                            />
                                                        </SortableItem>
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <NoResult
                                                title="Aucun article pour le moment"
                                                paddingSx="15px"
                                            />
                                        )}
                                        {dossier.articles && remisesInvisiblesAffichees
                                            ? dossier.articles
                                                  .filter(
                                                      (art) =>
                                                          art.id !== undefined && art.fMasqueCalcul,
                                                  )
                                                  .map((article) => (
                                                      <ArticlePrefacturation
                                                          article={article}
                                                          sx={{ marginTop: '10px' }}
                                                          icon={hiddenArticleIcon}
                                                      />
                                                  ))
                                            : null}
                                        {dossier.reductions && remisesInvisiblesAffichees
                                            ? dossier.reductions.map((reduction) => {
                                                  const articleReduction: Omit<
                                                      ArticleTarifDTOJsonldDonneesFacturationRead,
                                                      'id'
                                                  > & { id: number } = {
                                                      ...reduction,
                                                      id: Math.random(),
                                                      idAnnonce:
                                                          reduction.idAnnonce !== undefined
                                                              ? String(reduction.idAnnonce)
                                                              : undefined,
                                                  };
                                                  return (
                                                      <ArticlePrefacturation
                                                          article={articleReduction}
                                                          sx={{ marginTop: '10px' }}
                                                          icon={discountIcon}
                                                      />
                                                  );
                                              })
                                            : null}
                                    </Accordion>
                                </Box>
                            ))}
                    </Box>
                </SkeletonLoader>
            </Accordion>
        </Card>
    );
};

export default OrderDetailsSection;
